import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getPublicTeams, FrontendTeam } from '@/services/teams.api';
import TeamDetailModal from './TeamDetailModal';

export default function TeamSection() {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState<'executive' | 'advisory'>('executive');
  const [visible, setVisible] = useState(false);
  const [teams, setTeams] = useState<FrontendTeam[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState<FrontendTeam | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const isZh = i18n.language === 'zh';

  // Helper function to strip HTML tags from rich text for card hover display
  const stripHtmlTags = (html: string): string => {
    if (!html) return '';
    // First decode HTML entities, then strip tags
    const decoded = html
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&nbsp;/g, ' ');
    return decoded.replace(/<[^>]*>/g, '').trim();
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        setLoading(true);
        const response = await getPublicTeams();
        setTeams(response || []);
      } catch (error) {
        console.error('Failed to fetch teams:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTeams();
  }, []);

  const filtered = teams.filter((m) => m.role === activeTab);

  return (
    <>
    <section className="py-20 md:py-24 bg-white" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className={`text-center mb-10 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#2C7DA0]/10 text-[#2C7DA0] text-xs font-semibold mb-4 tracking-widest uppercase">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-team-line" />
            </div>
            {t('aboutPage.team.badge')}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#0A2540] mb-3">
            {t('aboutPage.team.title')}
          </h2>
          <p className="text-[#0A2540]/50 text-base max-w-lg mx-auto">
            {t('aboutPage.team.subtitle')}
          </p>
        </div>

        {/* Tab Switch */}
        <div className={`flex justify-center mb-10 transition-all duration-700 delay-100 ${visible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="flex items-center gap-1 bg-[#F7F9FC] p-1 rounded-xl">
            {(['executive', 'advisory'] as const).map((role) => (
              <button
                key={role}
                onClick={() => setActiveTab(role)}
                className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap
                  ${activeTab === role
                    ? 'bg-[#0A2540] text-white'
                    : 'text-[#0A2540]/60 hover:text-[#0A2540]'
                  }`}
              >
                {t(`aboutPage.team.${role}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Team Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="text-[#0A2540]/50">{t('common.loading')}</div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-[#0A2540]/50">{isZh ? '暂无团队成员' : 'No team members available'}</div>
          </div>
        ) : (
          <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-${activeTab === 'executive' ? '3' : '3'} xl:grid-cols-${activeTab === 'executive' ? '5' : '5'} gap-5 transition-all duration-700 delay-150 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            {filtered.map((member) => (
            <div
              key={member.id}
              className="group relative bg-[#F7F9FC] rounded-2xl p-6 text-center hover:bg-[#0A2540] transition-all duration-300 cursor-pointer"
              onClick={() => setSelectedMember(member)}
            >
              {/* LinkedIn button */}
              {member.linkedin && (
                <a
                  href={member.linkedin}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="absolute top-3 right-3 w-7 h-7 rounded-lg bg-white/0 group-hover:bg-white/15 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 cursor-pointer hover:bg-white/25 z-10"
                  title="LinkedIn Profile"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-linkedin-fill text-white/70 hover:text-white text-sm transition-colors" />
                  </div>
                </a>
              )}

              <div className="w-32 h-32 rounded-full overflow-hidden mx-auto mb-4 ring-2 ring-[#2C7DA0]/20 group-hover:ring-white/30 transition-all">
                {member.photo ? (
                  <img
                    src={member.photo}
                    alt={isZh ? member.name : member.nameEn}
                    className="w-full h-full object-cover object-top"
                  />
                ) : (
                  <div className="w-full h-full bg-[#2C7DA0]/10 flex items-center justify-center">
                    <i className="ri-user-line text-2xl text-[#2C7DA0]/30" />
                  </div>
                )}
              </div>
              <h3 className="font-bold text-[#0A2540] group-hover:text-white text-sm mb-1 transition-colors">
                {isZh ? member.name : member.nameEn}
              </h3>
              <p className="text-[#2C7DA0] group-hover:text-[#7EC8E3] text-xs font-medium mb-0.5 transition-colors">
                {isZh ? member.title : member.titleEn}
              </p>
              <p className="text-[#0A2540]/50 group-hover:text-white/50 text-xs transition-colors">
                {isZh ? member.institution : member.institutionEn}
              </p>
              {/* Bio on hover - Strip HTML tags for clean display */}
              <p className="mt-3 text-xs leading-relaxed text-[#0A2540]/0 group-hover:text-white/70 transition-all duration-300 line-clamp-3">
                {stripHtmlTags(isZh ? member.bio : member.bioEn)}
              </p>

              {/* LinkedIn link label at bottom on hover */}
              {member.linkedin && (
                <div className="mt-3 opacity-0 group-hover:opacity-100 transition-all duration-300">
                  <a
                    href={member.linkedin}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1 text-[10px] text-white/40 hover:text-[#7EC8E3] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-linkedin-box-line text-xs" />
                    </div>
                    LinkedIn
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-external-link-line text-xs" />
                    </div>
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>
        )}
      </div>
    </section>

    {selectedMember && (
      <TeamDetailModal
        member={selectedMember}
        onClose={() => setSelectedMember(null)}
      />
    )}
    </>
  );
}
